import React, { useState, useEffect } from 'react';
import { useActas } from '../../hooks/useActas';
import ActaList from './components/ActaList';
import ActaForm from './components/ActaForm';
import EditActaForm from './components/EditActaForm';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import ErrorMessage from '../../components/UI/ErrorMessage';
import { 
  PlusIcon, 
  DocumentTextIcon, 
  MagnifyingGlassIcon,
  UserIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ActaManagement = () => {
  const { 
    actas, 
    loading, 
    error, 
    createActa, 
    updateActa, 
    deleteActa,
    fetchActas,
    fetchActasByUsuario,
    fetchActasByEmpresa
  } = useActas();

  const [showActaForm, setShowActaForm] = useState(false);
  const [editingActa, setEditingActa] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    empresa_nombre: '',
    tipo_actividad: '',
    fecha_inicio: '',
    fecha_fin: '',
  });
  const [advancedFilter, setAdvancedFilter] = useState({
    type: 'all', // 'all', 'usuario', 'empresa'
    value: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Cargar actas cuando cambien los filtros avanzados
  useEffect(() => {
    if (advancedFilter.type === 'usuario' && advancedFilter.value) {
      fetchActasByUsuario(advancedFilter.value, filters);
    } else if (advancedFilter.type === 'empresa' && advancedFilter.value) {
      fetchActasByEmpresa(advancedFilter.value, filters);
    } else if (advancedFilter.type === 'all') {
      fetchActas(filters);
    }
  }, [advancedFilter, filters]);

  const handleCreateActa = async (actaData, archivos) => {
    try {
      //console.log('📝 handleCreateActa - Datos recibidos:', { 
      //  actaData, 
      //  archivos: archivos?.length,
      //  archivosDetalle: archivos?.map(a => ({ name: a.name, size: a.size }))
      //});
      
      // PASO 2: Llamar a createActa con los archivos
      await createActa(actaData, archivos);
      setShowActaForm(false);
      reloadActas();
    } catch (error) {
      //console.error('❌ Error en handleCreateActa:', error);
      throw error;
    }
  };

  const handleEditActa = (acta) => {
    setEditingActa(acta);
    setShowActaForm(true);
  };

  const handleUpdateActa = async (formData) => {
    try {
	console.log("handleUpdateActa - Iniciando actualizar", formData)
      	let dataToSend;
	if (formData instanceof FormData) {
		dataToSend = formData;
	} else if (formData.archivos_nuevos && formData.archivos_nuevos.length > 0) {
		dataToSend = new FormData();
		Object.keys(formData).forEach(key => {
			if (key !== 'archivos_nuevos' && key !== 'archivos_eliminar') {
				const value = formData[key];
				if (value !== null && value !== undefined && value !== '') {
					dataToSend.append(key, value);
				}
			}
		});
		formData.archivos_nuevos.forEach(archivo => {
			dataToSend.append('archivos_nuevos', archivo);
		});
		if (formData.archivos_eliminar && formData.archivos_eliminar.length > 0) {
			dataToSend.append('archivos_eliminar', JSON.stringify(formData.archivos_eliminar));
		}
	} else {
		dataToSend = { ...formData };
		delete dataToSend.archivos_nuevos;
		delete dataToSend.archivos_eliminar;
	}
	//console.log('llama el hook')
	// Llamar al hook
	await updateActa(editingActa.numero_consecutivo, dataToSend);
	//console.log('Despues del llamado del hook osea que se ve el console del hook')
	setEditingActa(null);
	setShowActaForm(false);
	reloadActas();
    } catch (error) {
      throw error;
    }
  };

  const handleCancelForm = () => {
    setShowActaForm(false);
    setEditingActa(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAdvancedFilterChange = (type, value) => {
    setAdvancedFilter({ type, value });
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilter({ type: 'all', value: '' });
    setFilters({
      empresa_nombre: '',
      tipo_actividad: '',
      fecha_inicio: '',
      fecha_fin: '',
    });
  };

  const reloadActas = () => {
    if (advancedFilter.type === 'usuario' && advancedFilter.value) {
      fetchActasByUsuario(advancedFilter.value, filters);
    } else if (advancedFilter.type === 'empresa' && advancedFilter.value) {
      fetchActasByEmpresa(advancedFilter.value, filters);
    } else {
      fetchActas(filters);
    }
  };

  // ✅ CORRECCIÓN: Filtrar actas con validaciones seguras
  const filteredActas = actas.filter(acta => {
    if (!acta) return false;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Validaciones seguras para cada campo
    const empresaNombre = acta.empresa_nombre || '';
    const empresaNit = acta.empresa_nit || '';
    const profesionalNombre = acta.profesional_nombre || '';
    const tipoActividad = acta.tipo_actividad || '';
    const descripcion = acta.descripcion || '';
    const nombrePersona = acta.nombre_persona || '';

    return (
      empresaNombre.toLowerCase().includes(searchLower) ||
      empresaNit.toLowerCase().includes(searchLower) ||
      profesionalNombre.toLowerCase().includes(searchLower) ||
      tipoActividad.toLowerCase().includes(searchLower) ||
      descripcion.toLowerCase().includes(searchLower) ||
      nombrePersona.toLowerCase().includes(searchLower) ||
      // También buscar por número de acta
      acta.numero_consecutivo?.toString().includes(searchTerm)
    );
  });

  // Función para obtener el texto del filtro activo
  const getActiveFilterText = () => {
    if (advancedFilter.type === 'usuario' && advancedFilter.value) {
      return `Usuario: ${advancedFilter.value}`;
    }
    if (advancedFilter.type === 'empresa' && advancedFilter.value) {
      return `Empresa: ${advancedFilter.value}`;
    }
    return 'Todas las actas';
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage error={error} onRetry={reloadActas} />;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Header con botón de acción */}
      <div className="mb-6">
        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Actas</h1>
              <p className="text-sm sm:text-base text-gray-600">
                {filteredActas.length} acta{filteredActas.length !== 1 ? 's' : ''} - {getActiveFilterText()}
              </p>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filtros
            </button>
            
            <button
              onClick={() => setShowActaForm(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Nueva Acta
            </button>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="mt-4">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por empresa, profesional, tipo, descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Filtros avanzados */}
        {showAdvancedFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Filtros Avanzados</h3>
              <div className="flex space-x-2">
                <button
                  onClick={clearAdvancedFilters}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <XMarkIcon className="h-3 w-3 mr-1" />
                  Limpiar
                </button>
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Filtro por tipo de búsqueda */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tipo de búsqueda
                </label>
                <select
                  value={advancedFilter.type}
                  onChange={(e) => handleAdvancedFilterChange(e.target.value, '')}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Todas las actas</option>
                  <option value="usuario">Por usuario</option>
                  <option value="empresa">Por empresa</option>
                </select>
              </div>

              {/* Campo de búsqueda específica */}
              {advancedFilter.type === 'usuario' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Username del usuario
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Ingresa username..."
                      value={advancedFilter.value}
                      onChange={(e) => handleAdvancedFilterChange('usuario', e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {advancedFilter.type === 'empresa' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    NIT de la empresa
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Ingresa NIT..."
                      value={advancedFilter.value}
                      onChange={(e) => handleAdvancedFilterChange('empresa', e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Filtro por tipo de actividad */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tipo de actividad
                </label>
                <select
                  value={filters.tipo_actividad}
                  onChange={(e) => handleFilterChange('tipo_actividad', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos los tipos</option>
                  <option value="auditoria">Auditoría</option>
                  <option value="consultoria">Consultoría</option>
                  <option value="asesoria">Asesoría</option>
                  <option value="revision">Revisión</option>
                  <option value="capacitacion">Capacitación</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              {/* Filtro por fechas */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Fecha inicio
                  </label>
                  <input
                    type="date"
                    value={filters.fecha_inicio}
                    onChange={(e) => handleFilterChange('fecha_inicio', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Fecha fin
                  </label>
                  <input
                    type="date"
                    value={filters.fecha_fin}
                    onChange={(e) => handleFilterChange('fecha_fin', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal para crear/editar acta */}
      {showActaForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-3 sm:p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingActa ? 'Editar Acta' : 'Crear Nueva Acta'}
                </h3>
                <button
                  onClick={handleCancelForm}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              {editingActa ? (
		<EditActaForm
			acta={editingActa}
			onSubmit={handleUpdateActa}
			onCancel={handleCancelForm}
		/>
		) : (
              	<ActaForm
                	acta={null}
                	onSubmit={handleCreateActa}
                	onCancel={handleCancelForm}
              	/>
	      )}
            </div>
          </div>
        </div>
      )}

      {/* Lista de actas */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <ActaList 
          actas={filteredActas}
          onEdit={handleEditActa}
          onDelete={deleteActa}
        />
      </div>

      {/* Mensaje cuando no hay resultados de búsqueda */}
      {filteredActas.length === 0 && actas.length > 0 && (
        <div className="text-center py-8">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron actas</h3>
          <p className="mt-1 text-sm text-gray-500">
            No hay actas que coincidan con "{searchTerm}".
          </p>
        </div>
      )}

      {/* Mensaje cuando no hay actas en absoluto */}
      {actas.length === 0 && !loading && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No hay actas registradas</h3>
          <p className="mt-2 text-sm text-gray-500">
            Comienza creando tu primera acta de servicio.
          </p>
          <button
            onClick={() => setShowActaForm(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Crear Primera Acta
          </button>
        </div>
      )}
    </div>
  );
};

export default ActaManagement;
